<template>
  <div>
    <h2 class="text-2xl font-semibold mb-4">贷款坏账预测 - 蒙特卡洛模拟</h2>

    <el-card shadow="hover" class="mb-4">
      <template #header>
        <div class="flex items-center justify-between flex-wrap gap-2">
          <h3 class="text-lg font-medium">模拟参数配置</h3>
          <div class="flex gap-2">
            <el-button type="success" plain @click="runSensitivity" :loading="loadingSens">运行敏感性分析</el-button>
            <el-button type="primary" @click="runSimulation" :loading="loading">开始模拟</el-button>
          </div>
        </div>
      </template>

      <el-form :model="form" label-position="top" class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <el-form-item label="贷款笔数 (N)">
          <el-input-number v-model="form.numLoans" :min="10" :max="10000" :step="100" class="w-full" />
        </el-form-item>
        <el-form-item :label="`单笔贷款金额 (${symbol})`">
          <el-input-number v-model="amountUI" :min="1000" :step="1000" class="w-full" />
        </el-form-item>
        <el-form-item label="违约概率 (PD, %)">
          <el-input-number v-model="pdPercent" :min="0.1" :max="100" :step="0.1" class="w-full" />
        </el-form-item>
        <el-form-item label="违约回收率 (Recovery Rate, %)">
          <el-input-number v-model="rrPercent" :min="0" :max="100" :step="5" class="w-full" />
        </el-form-item>
        <el-form-item label="模拟轮数 (Iterations)">
          <el-select v-model="form.iterations" class="w-full">
            <el-option label="1,000 次 (快速)" :value="1000" />
            <el-option label="5,000 次 (标准)" :value="5000" />
            <el-option label="10,000 次 (高精度)" :value="10000" />
            <el-option label="50,000 次 (极慢)" :value="50000" />
          </el-select>
        </el-form-item>
        <el-form-item label="随机种子 (可选)">
           <el-input v-model="form.seed" placeholder="留空则完全随机" />
        </el-form-item>
      </el-form>
      
      <el-alert type="info" :closable="false" show-icon title="模型说明">
        <div class="text-xs text-gray-500 mt-1 leading-relaxed">
          <p><strong>伯努利分布：</strong> 假设每笔贷款违约事件相互独立，且服从参数为 PD 的伯努利分布。</p>
          <p><strong>二项分布：</strong> 贷款组合中违约总数服从 B(N, PD)。</p>
          <p><strong>中心极限定理：</strong> 当 N 足够大时，总损失分布近似正态分布。</p>
        </div>
      </el-alert>
    </el-card>

    <el-collapse-transition>
      <div v-if="result" class="space-y-4">
        <!-- 结果指标卡片 -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <el-card shadow="never" body-class="p-4 text-center relative overflow-hidden">
            <div class="text-gray-500 text-sm mb-1">预期损失 (EL)</div>
            <div class="text-xl font-bold text-blue-600">{{ formatMoney(result.meanLoss) }}</div>
            <div class="text-xs text-gray-400 mt-1">理论值: {{ formatMoney(result.theoreticalMean) }}</div>
            <div class="absolute top-0 right-0 p-1 text-[10px] text-gray-300">期望</div>
          </el-card>
          <el-card shadow="never" body-class="p-4 text-center relative overflow-hidden">
            <div class="text-gray-500 text-sm mb-1">非预期损失 (UL)</div>
            <div class="text-xl font-bold text-orange-600">{{ formatMoney(result.stdDevLoss) }}</div>
            <div class="text-xs text-gray-400 mt-1">理论值: {{ formatMoney(result.theoreticalStdDev) }}</div>
            <div class="absolute top-0 right-0 p-1 text-[10px] text-gray-300">标准差</div>
          </el-card>
          <el-card shadow="never" body-class="p-4 text-center">
            <div class="text-gray-500 text-sm mb-1">VaR (95%)</div>
            <div class="text-xl font-bold text-red-600">{{ formatMoney(result.var95) }}</div>
            <div class="text-xs text-gray-400 mt-1">95% 置信度最大损失</div>
          </el-card>
          <el-card shadow="never" body-class="p-4 text-center">
            <div class="text-gray-500 text-sm mb-1">VaR (99%)</div>
            <div class="text-xl font-bold text-red-800">{{ formatMoney(result.var99) }}</div>
            <div class="text-xs text-gray-400 mt-1">99% 置信度最大损失</div>
          </el-card>
        </div>

        <!-- 图表区域 -->
        <el-card shadow="never">
          <el-tabs v-model="activeTab" @tab-change="handleTabChange">
            <el-tab-pane label="损失分布 (PDF/CLT)" name="dist">
              <div ref="chartDistEl" style="height: 400px;"></div>
              <div class="text-xs text-gray-500 mt-2 text-center">
                直方图展示模拟结果的概率密度；红线为基于中心极限定理的正态分布拟合曲线。
              </div>
            </el-tab-pane>
            <el-tab-pane label="大数定律收敛 (LLN)" name="conv">
              <div ref="chartConvEl" style="height: 400px;"></div>
              <div class="text-xs text-gray-500 mt-2 text-center">
                展示随着模拟次数增加，样本均值逐渐收敛于理论期望的过程。
              </div>
            </el-tab-pane>
            <el-tab-pane label="模拟散点图" name="scatter">
              <div ref="chartScatterEl" style="height: 400px;"></div>
              <div class="text-xs text-gray-500 mt-2 text-center">
                展示前 2000 次模拟的随机结果，直观体现随机性。
              </div>
            </el-tab-pane>
            <el-tab-pane label="敏感性分析" name="sens">
              <div v-if="!sensResult" class="h-[400px] flex items-center justify-center text-gray-400">
                请点击上方“运行敏感性分析”按钮
              </div>
              <div v-else ref="chartSensEl" style="height: 400px;"></div>
              <div class="text-xs text-gray-500 mt-2 text-center">
                展示违约概率 (PD) 变化对预期损失 (EL) 和风险价值 (VaR) 的影响。
              </div>
            </el-tab-pane>
          </el-tabs>
        </el-card>
      </div>
    </el-collapse-transition>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue'
import * as echarts from 'echarts'
import { useCurrency } from '@/composables/useCurrency'

const { toDisplay, toBase, symbol, format } = useCurrency()
const formatMoney = (v) => format(v)

const loading = ref(false)
const loadingSens = ref(false)
const result = ref(null)
const sensResult = ref(null)
const activeTab = ref('dist')

const chartDistEl = ref(null)
const chartConvEl = ref(null)
const chartScatterEl = ref(null)
const chartSensEl = ref(null)

let chartDist = null
let chartConv = null
let chartScatter = null
let chartSens = null

const form = ref({
  numLoans: 1000,
  amountPerLoan: 10000,
  pd: 0.05, // 5%
  recoveryRate: 0.20, // 20%
  iterations: 5000,
  seed: ''
})

// UI Computed Properties
const amountUI = computed({
  get: () => toDisplay(form.value.amountPerLoan),
  set: (v) => form.value.amountPerLoan = toBase(v)
})
const pdPercent = computed({
  get: () => Number((form.value.pd * 100).toFixed(2)),
  set: (v) => form.value.pd = v / 100
})
const rrPercent = computed({
  get: () => Number((form.value.recoveryRate * 100).toFixed(2)),
  set: (v) => form.value.recoveryRate = v / 100
})

const runSimulation = async () => {
  loading.value = true
  setTimeout(() => {
    try {
      simulate()
    } finally {
      loading.value = false
    }
  }, 100)
}

const simulate = () => {
  const N = form.value.numLoans
  const P = form.value.amountPerLoan
  const PD = form.value.pd
  const LGD = 1 - form.value.recoveryRate
  const M = form.value.iterations
  
  const losses = []
  const runningMeans = []
  let cumulativeSum = 0
  
  // Theoretical Values
  const theoreticalMean = N * P * PD * LGD
  const theoreticalVar = N * PD * (1 - PD) * Math.pow(P * LGD, 2)
  const theoreticalStdDev = Math.sqrt(theoreticalVar)

  for (let i = 0; i < M; i++) {
    let defaults = 0
    // Explicit simulation for demonstration
    for (let j = 0; j < N; j++) {
      if (Math.random() < PD) defaults++
    }
    const loss = defaults * P * LGD
    losses.push(loss)
    
    cumulativeSum += loss
    if (i % 10 === 0 || i < 100) { // Sample for chart performance
        runningMeans.push({ i: i + 1, mean: cumulativeSum / (i + 1) })
    }
  }
  // Ensure last point is added
  runningMeans.push({ i: M, mean: cumulativeSum / M })

  // Sort for VaR
  const sortedLosses = [...losses].sort((a, b) => a - b)
  const meanLoss = cumulativeSum / M
  const variance = losses.reduce((a, b) => a + Math.pow(b - meanLoss, 2), 0) / M
  const stdDevLoss = Math.sqrt(variance)
  const var95 = sortedLosses[Math.floor(M * 0.95)]
  const var99 = sortedLosses[Math.floor(M * 0.99)]

  result.value = {
    meanLoss, stdDevLoss, var95, var99,
    theoreticalMean, theoreticalStdDev,
    losses: sortedLosses, // Sorted for histogram
    rawLosses: losses,    // Unsorted for scatter
    runningMeans
  }

  nextTick(() => {
    handleTabChange(activeTab.value)
  })
}

const runSensitivity = async () => {
  loadingSens.value = true
  activeTab.value = 'sens'
  setTimeout(() => {
    try {
      const N = form.value.numLoans
      const P = form.value.amountPerLoan
      const LGD = 1 - form.value.recoveryRate
      const basePD = form.value.pd
      const M = 2000 // Faster iterations for sensitivity
      
      const steps = []
      // Range from 0.5x to 2.0x of base PD, or fixed range 1% to 10% if base is small
      let startPD = Math.max(0.001, basePD * 0.5)
      let endPD = Math.min(0.99, basePD * 2.0)
      if (endPD - startPD < 0.05) { endPD = startPD + 0.1 } // Ensure some range
      
      const stepCount = 10
      const stepSize = (endPD - startPD) / stepCount
      
      for (let k = 0; k <= stepCount; k++) {
        const currentPD = startPD + k * stepSize
        const losses = []
        for (let i = 0; i < M; i++) {
          let defaults = 0
          for (let j = 0; j < N; j++) {
            if (Math.random() < currentPD) defaults++
          }
          losses.push(defaults * P * LGD)
        }
        losses.sort((a, b) => a - b)
        const mean = losses.reduce((a,b)=>a+b,0)/M
        const var95 = losses[Math.floor(M * 0.95)]
        const var99 = losses[Math.floor(M * 0.99)]
        steps.push({ pd: currentPD, mean, var95, var99 })
      }
      sensResult.value = steps
      nextTick(() => renderSensChart())
    } finally {
      loadingSens.value = false
    }
  }, 100)
}

const handleTabChange = (tab) => {
  nextTick(() => {
    if (tab === 'dist') renderDistChart()
    if (tab === 'conv') renderConvChart()
    if (tab === 'scatter') renderScatterChart()
    if (tab === 'sens' && sensResult.value) renderSensChart()
  })
}

// --- Chart Renderers ---

const renderDistChart = () => {
  if (!result.value || !chartDistEl.value) return
  if (!chartDist) chartDist = echarts.init(chartDistEl.value)
  
  const data = result.value.losses
  const min = data[0]
  const max = data[data.length - 1]
  const binCount = 50
  const binSize = (max - min) / binCount || 100
  
  const bins = new Array(binCount).fill(0)
  const binEdges = []
  for(let i=0; i<=binCount; i++) binEdges.push(min + i * binSize)
  
  data.forEach(val => {
    let idx = Math.floor((val - min) / binSize)
    if (idx >= binCount) idx = binCount - 1
    bins[idx]++
  })
  
  const xAxisData = binEdges.slice(0, -1).map(v => v + binSize/2) // Center of bin
  const yAxisData = bins.map(v => v) // Count
  
  // Normal Distribution Curve
  const mu = result.value.meanLoss
  const sigma = result.value.stdDevLoss
  const normalData = xAxisData.map(x => {
    const p = (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2))
    return p * data.length * binSize // Scale PDF to Count
  })

  chartDist.setOption({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: ['频数', '正态拟合'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: xAxisData.map(v => Math.round(toDisplay(v))), name: '损失金额' },
    yAxis: { type: 'value', name: '频数' },
    series: [
      {
        name: '频数', type: 'bar', data: yAxisData, barWidth: '100%', itemStyle: { color: '#93c5fd' },
        markLine: {
          symbol: 'none',
          label: { position: 'start' },
          data: [
            { xAxis: bins.length * ((result.value.meanLoss - min)/(max-min)), label: { formatter: 'EL' }, lineStyle: { color: 'blue', width: 2 } },
            { xAxis: bins.length * ((result.value.var95 - min)/(max-min)), label: { formatter: 'VaR 95%' }, lineStyle: { color: 'red', type: 'dashed' } },
            { xAxis: bins.length * ((result.value.var99 - min)/(max-min)), label: { formatter: 'VaR 99%' }, lineStyle: { color: 'darkred', type: 'dashed' } }
          ]
        }
      },
      {
        name: '正态拟合', type: 'line', data: normalData, smooth: true, symbol: 'none', lineStyle: { color: '#ef4444', width: 2 }
      }
    ]
  })
}

const renderConvChart = () => {
  if (!result.value || !chartConvEl.value) return
  if (!chartConv) chartConv = echarts.init(chartConvEl.value)
  
  const data = result.value.runningMeans
  const xData = data.map(d => d.i)
  const yData = data.map(d => toDisplay(d.mean))
  const theoretical = toDisplay(result.value.theoreticalMean)

  chartConv.setOption({
    tooltip: { trigger: 'axis' },
    legend: { data: ['模拟均值', '理论期望'] },
    xAxis: { type: 'category', data: xData, name: '模拟次数' },
    yAxis: { type: 'value', name: '平均损失', scale: true },
    series: [
      { name: '模拟均值', type: 'line', data: yData, showSymbol: false, lineStyle: { width: 1 } },
      { name: '理论期望', type: 'line', data: new Array(xData.length).fill(theoretical), showSymbol: false, lineStyle: { type: 'dashed', color: 'green' } }
    ]
  })
}

const renderScatterChart = () => {
  if (!result.value || !chartScatterEl.value) return
  if (!chartScatter) chartScatter = echarts.init(chartScatterEl.value)
  
  // Take first 2000 points
  const raw = result.value.rawLosses.slice(0, 2000)
  const data = raw.map((loss, idx) => [idx + 1, toDisplay(loss)])

  chartScatter.setOption({
    tooltip: { trigger: 'item', formatter: (p) => `第 ${p.data[0]} 次: ${formatMoney(p.data[1])}` },
    xAxis: { type: 'value', name: '模拟序号', max: 2000 },
    yAxis: { type: 'value', name: '损失金额', scale: true },
    series: [
      { type: 'scatter', data: data, symbolSize: 3, itemStyle: { color: '#8b5cf6', opacity: 0.6 } }
    ]
  })
}

const renderSensChart = () => {
  if (!sensResult.value || !chartSensEl.value) return
  if (!chartSens) chartSens = echarts.init(chartSensEl.value)
  
  const data = sensResult.value
  const xData = data.map(d => (d.pd * 100).toFixed(2))
  
  chartSens.setOption({
    tooltip: { trigger: 'axis' },
    legend: { data: ['预期损失 (EL)', 'VaR 99%'] },
    xAxis: { type: 'category', data: xData, name: '违约概率 PD (%)' },
    yAxis: { type: 'value', name: '金额' },
    series: [
      { name: '预期损失 (EL)', type: 'line', data: data.map(d => toDisplay(d.mean)), itemStyle: { color: 'blue' } },
      { name: 'VaR 99%', type: 'line', data: data.map(d => toDisplay(d.var99)), itemStyle: { color: 'red' } }
    ]
  })
}

watch(() => form.value.iterations, () => { result.value = null })
</script>
